import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageSquare, 
  Send, 
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  Bot,
  User,
  Loader2,
  Ticket,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getHeaders 
} from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('chat');
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });
  const [ticketReply, setTicketReply] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
    loadTickets();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadChatHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/support/chat-history?limit=50`, {
        headers: getHeaders()
      });
      const data = await res.json();
      
      // Convert history to messages format
      const messages = [];
      (data.history || []).reverse().forEach(h => {
        messages.push({ role: 'user', content: h.user_message, timestamp: h.created_at });
        messages.push({ role: 'assistant', content: h.ai_response, timestamp: h.created_at });
      });
      setChatMessages(messages);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const loadTickets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/support/tickets`, {
        headers: getHeaders()
      });
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/support/chat?message=${encodeURIComponent(userMessage)}`, {
        method: 'POST',
        headers: getHeaders()
      });
      const data = await res.json();
      
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response, 
        source: data.source,
        suggestTicket: data.suggest_ticket,
        timestamp: new Date().toISOString() 
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        subject: newTicket.subject,
        description: newTicket.description,
        category: newTicket.category,
        priority: newTicket.priority
      });

      const res = await fetch(`${API_URL}/api/support/tickets?${params}`, {
        method: 'POST',
        headers: getHeaders()
      });
      const data = await res.json();
      
      toast.success(`Ticket ${data.ticket_number} créé avec succès`);
      setNewTicket({ subject: '', description: '', category: 'general', priority: 'medium' });
      loadTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Erreur lors de la création du ticket');
    } finally {
      setLoading(false);
    }
  };

  const sendTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${selectedTicket.id}/messages?content=${encodeURIComponent(ticketReply)}`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      toast.success('Message envoyé');
      setTicketReply('');
      // Reload ticket details
      const ticketRes = await fetch(`${API_URL}/api/support/tickets/${selectedTicket.id}`, {
        headers: getHeaders()
      });
      const ticketData = await ticketRes.json();
      setSelectedTicket(ticketData);
      loadTickets();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      waiting_customer: 'bg-purple-100 text-purple-700',
      resolved: 'bg-emerald-100 text-emerald-700',
      closed: 'bg-slate-100 text-slate-600'
    };
    const labels = {
      open: 'Ouvert',
      in_progress: 'En cours',
      waiting_customer: 'En attente',
      resolved: 'Résolu',
      closed: 'Fermé'
    };
    return <Badge className={styles[status] || styles.open}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-blue-100 text-blue-700',
      high: 'bg-orange-100 text-orange-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return <Badge variant="outline" className={styles[priority]}>{priority}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
            Support
          </h1>
          <p className="text-slate-600 mt-1">Assistance et tickets de support</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="chat" className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              Chatbot IA
            </TabsTrigger>
            <TabsTrigger value="tickets" className="flex items-center gap-2">
              <Ticket className="w-4 h-4" />
              Mes Tickets
            </TabsTrigger>
            <TabsTrigger value="new" className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Nouveau
            </TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat" className="mt-6">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-orange-500" />
                  Assistant Flitflow
                </CardTitle>
                <CardDescription>
                  Posez vos questions, je suis là pour vous aider 24/7
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col p-0">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {chatMessages.length === 0 && (
                    <div className="text-center text-slate-500 py-8">
                      <Bot className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Bonjour ! Comment puis-je vous aider ?</p>
                      <div className="flex flex-wrap justify-center gap-2 mt-4">
                        {['Comment créer une mission ?', 'Commission Flitflow ?', 'Vérification KYC'].map((q) => (
                          <Button
                            key={q}
                            variant="outline"
                            size="sm"
                            onClick={() => setChatInput(q)}
                          >
                            {q}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {chatMessages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] rounded-lg p-3 ${
                        msg.role === 'user' 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-slate-100 text-slate-900'
                      }`}>
                        <div className="flex items-start gap-2">
                          {msg.role === 'assistant' && (
                            <Bot className="w-4 h-4 mt-1 text-orange-500 shrink-0" />
                          )}
                          <div>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {msg.suggestTicket && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2"
                                onClick={() => setActiveTab('new')}
                              >
                                Créer un ticket
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 rounded-lg p-3">
                        <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Input */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Tapez votre message..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                      disabled={loading}
                    />
                    <Button onClick={sendChatMessage} disabled={loading || !chatInput.trim()}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Ticket List */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle>Mes tickets</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {tickets.length === 0 ? (
                      <div className="p-4 text-center text-slate-500">
                        <Ticket className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p>Aucun ticket</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {tickets.map((ticket) => (
                          <button
                            key={ticket.id}
                            className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                              selectedTicket?.id === ticket.id ? 'bg-slate-50' : ''
                            }`}
                            onClick={() => setSelectedTicket(ticket)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900 truncate">{ticket.subject}</p>
                                <p className="text-xs text-slate-500 mt-1">{ticket.ticket_number}</p>
                              </div>
                              {getStatusBadge(ticket.status)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Ticket Detail */}
              <div className="lg:col-span-2">
                {selectedTicket ? (
                  <Card>
                    <CardHeader className="border-b">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{selectedTicket.subject}</CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <span>{selectedTicket.ticket_number}</span>
                            <span>•</span>
                            <span>{new Date(selectedTicket.created_at).toLocaleDateString()}</span>
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          {getPriorityBadge(selectedTicket.priority)}
                          {getStatusBadge(selectedTicket.status)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {/* Messages */}
                      <div className="max-h-[400px] overflow-y-auto p-4 space-y-4">
                        {selectedTicket.messages?.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] rounded-lg p-3 ${
                              msg.sender_type === 'user' 
                                ? 'bg-orange-500 text-white' 
                                : 'bg-slate-100 text-slate-900'
                            }`}>
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${
                                msg.sender_type === 'user' ? 'text-orange-200' : 'text-slate-400'
                              }`}>
                                {new Date(msg.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Reply Input */}
                      {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                        <div className="border-t p-4">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Votre réponse..."
                              value={ticketReply}
                              onChange={(e) => setTicketReply(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && sendTicketReply()}
                            />
                            <Button onClick={sendTicketReply} disabled={loading || !ticketReply.trim()}>
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="h-full flex items-center justify-center">
                    <div className="text-center text-slate-500 p-8">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Sélectionnez un ticket pour voir les détails</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* New Ticket Tab */}
          <TabsContent value="new" className="mt-6">
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle>Créer un ticket</CardTitle>
                <CardDescription>
                  Décrivez votre problème et notre équipe vous répondra rapidement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sujet</label>
                  <Input
                    placeholder="Résumé de votre demande"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Catégorie</label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-slate-200"
                      value={newTicket.category}
                      onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    >
                      <option value="general">Général</option>
                      <option value="technical">Technique</option>
                      <option value="billing">Facturation</option>
                      <option value="kyc">KYC / Vérification</option>
                      <option value="mission">Mission</option>
                      <option value="payment">Paiement</option>
                      <option value="account">Compte</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Priorité</label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-slate-200"
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                    >
                      <option value="low">Basse</option>
                      <option value="medium">Moyenne</option>
                      <option value="high">Haute</option>
                      <option value="urgent">Urgente</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    placeholder="Décrivez votre problème en détail..."
                    rows={6}
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  />
                </div>

                <Button 
                  className="w-full" 
                  onClick={createTicket}
                  disabled={loading || !newTicket.subject.trim() || !newTicket.description.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Créer le ticket
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SupportPage;
